import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledTextField,
  ControlledDateTimeField,
  ControlledMediaUpload,
  ControlledSelect,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { useObservationLogs } from '@/hooks'
import { FormComponentProps } from '@/types'
import { schema } from './validationSchema'
import {
  BAT_COMMON_DWARF_BAT,
  COURTSHIP_TERRITORY_SPECIE_OPTIONS,
} from '../fieldsOptions'
import { SelectOption } from '@/components'
import { useEffect } from 'react'

export const CourtshipTerritoryForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      notes: '',
      observed_at: '',
      photos: undefined,
      related_observation_log_id: null,
    },
    values: {
      ...defaultValues,
      specie: defaultValues?.specie ?? BAT_COMMON_DWARF_BAT,
    },
    resolver: yupResolver(schema),
  })

  const specie = methods.watch('specie')

  useEffect(() => {
    if (specie !== '' && specie !== defaultValues?.specie) {
      methods.setValue('related_observation_log_id', null)
    }
  }, [specie])

  const { data: observationLogsData } = useObservationLogs(
    {
      research_visit_event: defaultValues?.research_visit_event_id
        ? defaultValues?.research_visit_event_id.toString()
        : undefined,
      specie: specie ?? '',
      isPlaceFound: true,
      limit: 100,
      offset: 0,
    },
    !!(defaultValues?.research_visit_event_id && specie)
  )

  const observationLogOptions: SelectOption[] = observationLogsData?.results
    ? [
        { value: '', name: '' },
        ...observationLogsData.results
          .filter((log) => log.id !== defaultValues?.id)
          .map((log) => ({
            value: log.id.toString(),
            name: `#${log.id} - ${log.specie || 'Unknown specie'}`,
          })),
      ]
    : [
        { value: '', name: '' },
        ...(defaultValues?.related_observation_log_id
          ? [
              {
                value:
                  defaultValues?.related_observation_log_id.toString() ?? '',
                name: `#${defaultValues?.related_observation_log_id ?? ''}`,
              },
            ]
          : []),
      ]

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  return (
    <FormProvider {...methods}>
      <Stack spacing={4} mt={4} width="100%">
        <Box width="25%">
          <ControlledSelect
            control={methods.control}
            name="specie"
            label={t('observation-log.fieldLabels.specie')}
            options={COURTSHIP_TERRITORY_SPECIE_OPTIONS}
            errorMessagePrefix=""
          />
        </Box>
        <ControlledSelect
          control={methods.control}
          name="related_observation_log_id"
          label={t('observation-log.fieldLabels.relatedObservationLogId')}
          options={observationLogOptions}
          errorMessagePrefix=""
        />
        <ControlledTextField
          control={methods.control}
          name="notes"
          placeholder={t('observation-log.fieldLabels.notes')}
          multiline={true}
          rows={3}
          errorMessagePrefix=""
        />
        <ControlledDateTimeField
          name="observed_at"
          control={methods.control}
          label={t('observation-log.fieldLabels.observedAt')}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          errorMessagePrefix=""
        />
        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
